<script setup lang="ts">
import MarketPrice from "./MarketPrice.vue";
import { IMarket } from "~/types/sports/market";
import { useBetbook } from "~/store/betbook";
import { IOutcome } from "~/types/sports/outcome";
import MarketHandicapGrouping from "./MarketHandicapGrouping.vue";
import { useSettings } from "~/store/settings";
import MarketCombosLayout from "./MarketCombosLayout.vue";

interface Props {
	eventId: number;
	marketId: string;
	selectedMarket: string | null;
	isSimpleView?: boolean;
	buildABetSelections: string[];
	market: IMarket;
}

const store = useBetbook();
const settings = useSettings();
const emits = defineEmits(["handleBuildABetSelection"]);
const props = defineProps<Props>();
const event = computed(() => store.getEvent(props.eventId));
const outcomes = computed(() => {
	try {
		if (props.market.handicap !== 0 || props.market.isSquashedParent) {
			let outcomes = [...store.outcomes.values()]
				.filter((x) => x.marketId === props.marketId)
				.filter(
					(o) => store.markets.get(o.originalMarketId) && store.markets.get(o.originalMarketId)?.shouldDisplay === true && o.shouldDisplay === true
				)
				.sort((a, b) => a.index - b.index);
			return outcomes;
		}
		let outcomes: IOutcome[];
		if (event.value.outcomes[props.marketId].length <= 3) {
			outcomes = event.value.outcomes[props.marketId];
		} else {
			outcomes = event.value.outcomes[props.marketId].filter((x) => x.shouldDisplay === true);
		}
		if (outcomes[0].handicap) {
			return outcomes.sort((a, b) => a.index - b.index);
		}

		return outcomes.sort((a, b) => a.index - b.index);
	} catch (e) {
		return null;
	}
});

const providerMarketGroups = computed(() => {
	const info = props.market.additionalInfo?.ProviderMarketGroups;
	try {
		return info && info !== "null" ? JSON.parse(info) : [];
	} catch (error) {
		return [];
	}
});

const isHandicapGroupingAllowed = computed(
	() => props.market.name.toLowerCase().includes("handicap") && event.value.sportId?.toLowerCase() !== "soccer"
);

const shouldShowHandicapGrouping = computed(() => {
	return (
		outcomes.value?.some((o) => o.sbv) &&
		!providerMarketGroups.value.includes("combo") &&
		!isHandicapGroupingAllowed.value &&
		!props.market.name.toLowerCase().includes("total") &&
		settings.hasFeature("IsNewMoreMarketsEnabled")
	);
});

const isComboLayout = computed(
	() =>
		settings.hasFeature("IsComboGroupingEnabled") &&
		providerMarketGroups.value.length &&
		providerMarketGroups?.value.includes("combo") &&
		outcomes.value &&
		!props.market.name.toLowerCase().includes("draw or over") &&
		!props.market.name.toLowerCase().includes("draw or under") &&
		!props.market.name.toLowerCase().includes("exact goals")
);

const isThreeLayoutCols = computed(
	() =>
		outcomes.value?.some((o) => ["draw", "none"].some((term) => o.displayName.toLowerCase().includes(term))) &&
		props.market.name.toLowerCase().replace(/-+/g, " ").replace(/\s+/g, "-").includes("3-way")
);
</script>

<template>
	<MarketCombosLayout v-if="isComboLayout && outcomes" :outcomes="outcomes" :market="market">
		<template v-slot="{ outcome }">
			<MarketPrice
				:isSimpleView="isSimpleView"
				:key="outcome.outcomeId"
				:eventId="eventId"
				:outcome="outcome"
				:market="market"
				:selectedMarket="selectedMarket"
				:buildABetSelections="buildABetSelections"
				:isPriceOnly="true"
				@handleBuildABetSelection="(x) => emits('handleBuildABetSelection', x)"
			/>
		</template>
	</MarketCombosLayout>
	<MarketHandicapGrouping :outcomes="outcomes" :market="market" v-if="outcomes && shouldShowHandicapGrouping">
		<template v-slot="{ outcome }">
			<MarketPrice
				:isSimpleView="isSimpleView"
				:key="outcome.outcomeId"
				:eventId="eventId"
				:outcome="outcome"
				:market="market"
				:selectedMarket="selectedMarket"
				:buildABetSelections="buildABetSelections"
				:isPriceOnly="true"
				@handleBuildABetSelection="(x) => emits('handleBuildABetSelection', x)"
			/>
		</template>
	</MarketHandicapGrouping>
	<div
		v-else-if="!isComboLayout"
		:class="[
			{ 'grid p-1 rounded-b-lg shadow-sm bg-light-50 gap-1 dark:bg-dark-800': !isSimpleView },
			{ 'grid gap-1': isSimpleView },
			outcomes?.length === 3 || isThreeLayoutCols ? 'grid-cols-3' : 'grid-cols-2',
		]"
	>
		<MarketPrice
			v-for="outcome in outcomes"
			:isSimpleView="isSimpleView"
			:key="outcome.outcomeId"
			:eventId="eventId"
			:outcome="outcome"
			:market="market"
			:selectedMarket="selectedMarket"
			:buildABetSelections="buildABetSelections"
			@handleBuildABetSelection="(x) => emits('handleBuildABetSelection', x)"
		/>
	</div>
</template>
